import { useFormik } from "formik";
import * as Yup from "yup";
import { InputTextarea } from "primereact/inputtextarea";
import { Button } from "primereact/button";
import { useState, useRef, useEffect } from "react";
import ComplaintService from "../../services/complaint/ComplaintService";
import { Toast } from "primereact/toast";
import { classNames } from "primereact/utils";
import { useNavigate } from "react-router-dom";
import { Dropdown } from "primereact/dropdown";
import { InputNumber } from "primereact/inputnumber";
import { Checkbox } from "primereact/checkbox";
import { InputText } from "primereact/inputtext";
import { ImageTemplateDemo } from "../images/file-upload-template";

interface InternalComplaintActionRegisterProps {
  isUpdate?: boolean;
  handleOnSuccess?: () => void;
  updateComplaintInfo?: any;
  selectedComplaint?: any;
}

const InternalComplaintActionRegister: React.FC<InternalComplaintActionRegisterProps> = ({ 
  isUpdate = false, 
  handleOnSuccess, 
  updateComplaintInfo,
  selectedComplaint,
}) => {
  const [imageFile, setImageFile] = useState<any>(null);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [forceResolved, setForceResolved] = useState(false);
  const toast = useRef<Toast>(null);
  const [hoveredField, setHoveredField] = useState<string | null>(null);
  const navigate = useNavigate();
  const [loginUser, setLoginUser] = useState<any>(null);
  const [containerType,setcontainerType] = useState<string>('Complaints');

  useEffect(() => {
    const userData = localStorage.getItem('user');
    if (userData) {
      const user = JSON.parse(userData);
      setLoginUser(user);
    }
  }, []);

  useEffect(() => {
    if (selectedComplaint && loginUser) {
      formik.setValues({
        id: null,
        internalComplaintId: selectedComplaint.id || null,
        actionDescription: '',
        image: null,
        actionAttachmentId: null,
        remarks: '',
        correctiveAction: '',
        preventiveAction: '',
        actionBy: loginUser.userID || null, 
        statusId: 1,
        severity: selectedComplaint.severity || '',
        actionDate: new Date().toISOString().split('T')[0],
        copqInRs: null,
        createdBy: loginUser.userID || 0, 
        createdDate: new Date().toISOString(),
        updatedBy: loginUser.userID || 0, 
        updatedDate: new Date().toISOString(),
        customerName: selectedComplaint.customerName || '',
        createdByName: loginUser.name || '', 
        updatedByName: loginUser.name || '', 
        mobile: selectedComplaint.mobile || ''
      });

      if (selectedComplaint.attachmentId) {
        setImageFile({ name: `Attachment_${selectedComplaint.attachmentId}` });
      }
    }
  }, [selectedComplaint, loginUser]);

  const severityOptions = [
    { label: 'High', value: 'High' },
    { label: 'Medium', value: 'Medium' },
    { label: 'Low', value: 'Low' }
  ];

  const statusOptions = [
    { label: 'New', value: 1 },
    { label: 'In Progress', value: 2 },
    { label: 'Resolved', value: 3 }
  ];

  const validationSchema = Yup.object({
    actionDescription: Yup.string().required("Action description is required"),
    statusId: Yup.number().required("Status is required")
  });

  const formik = useFormik({
    initialValues: {
      id: null,
      internalComplaintId: null,
      actionDescription: '',
      image:null,
      actionAttachmentId: null,
      remarks: '',
      correctiveAction: '',
      preventiveAction: '',
      actionBy: null,
      statusId: 1,
      severity: '',
      actionDate: new Date().toISOString().split('T')[0],
      copqInRs: null,
      createdBy: 0,
      createdDate: new Date().toISOString(),
      updatedBy: 0,
      updatedDate: new Date().toISOString(),
      customerName: '',
      createdByName: '',
      updatedByName: '',
      mobile: ''
    },
    validationSchema: validationSchema,
    onSubmit: async (values, { resetForm }) => {
      if (isSubmitted) return;
      setIsSubmitted(true);

      try {
        const formData = {
          internalComplaintId: values.internalComplaintId,
          actionDescription: values.actionDescription,
          image : imageFile,
          actionAttachmentId: null,
          remarks: values.remarks,
          correctiveAction: values.correctiveAction,
          preventiveAction: values.preventiveAction,
          actionBy: loginUser?.userID || 1, 
          statusId: values.statusId,
          severity: values.severity,
          actionDate: new Date(values.actionDate).toISOString(),
          copqInRs: values.copqInRs,
          createdBy: loginUser?.userID || 1, 
          createdDate: new Date().toISOString(),
          updatedBy: loginUser?.userID || 1, 
          updatedDate: new Date().toISOString(),
          customerName: values.customerName,
          createdByName: loginUser?.name || 'System', 
          updatedByName: loginUser?.name || 'System', 
          mobile: values.mobile,
          containerType: containerType
        };

        console.log("Submitting data to backend:", formData);

        const response = await ComplaintService.CreateInternalComplaintAction(formData);
        
        if (response && response.isSuccess) {
          showMessage('Success', 'Internal complaint action created successfully', 'success');
          
          if (handleOnSuccess) {
            handleOnSuccess();
          }
          resetForm();
          setImageFile(null);
          setForceResolved(false);
          
          if (!isUpdate) {
            navigate('/internal-complaint-actions-main'); 
          }
        } else {
          showMessage('Error', response?.message || 'Failed to create internal complaint action', 'error');
        }
      } catch (error) {
        console.error("Error creating internal complaint action:", error);
        showMessage('Error', 'Failed to create internal complaint action', 'error');
      } finally {
        setIsSubmitted(false);
      }
    }
  });

  useEffect(() => {
    if (forceResolved) {
      formik.setFieldValue('statusId', 3); 
      return;
    }

    const correctiveFilled = formik.values.correctiveAction.trim() !== '';
    const preventiveFilled = formik.values.preventiveAction.trim() !== '';

    if (correctiveFilled && preventiveFilled) {
      formik.setFieldValue('statusId', 3); 
    } else if (correctiveFilled || preventiveFilled) {
      formik.setFieldValue('statusId', 2); 
    } else {
      formik.setFieldValue('statusId', 1); 
    }
  }, [formik.values.correctiveAction, formik.values.preventiveAction, forceResolved]);

  const showMessage = (label: string, message: string, severity: 'success' | 'error') => {
    toast.current?.show({ severity, summary: label, detail: message, life: 3000 });
  };

  const fieldHoverStyle = (fieldName: string) => ({
    border: hoveredField === fieldName ? '2px solid #6366F1' : '1px solid #ced4da',
    borderRadius: '6px',
    transition: 'border 0.2s ease-in-out',
    width: '100%',
    minHeight: '40px'
  });

  const handelfileUpload = (query:any) => {
    const file = query;
    setImageFile(file)
  }

  const handelfileSizeExceeded = (message:string) => {
    toast.current?.show({
        severity: 'info',
        summary: 'InfoError',
        detail: message,
        life: 3000
    });
  }

  return (
    <>
      <Toast ref={toast} position="top-center" />
      <div className="w-full p-3 md:p-4 border-round">
        <div className="card p-0 border-round w-full">
          <div className="p-3">
            <h5 className="border-round mb-3 order-top-color p-2 panel-header-text text-center shadow-3">
              Internal Complaint Action
            </h5>

            <form onSubmit={formik.handleSubmit}>
              <div className="p-fluid formgrid grid">
                {/* Hidden Fields */}
                <input type="hidden" name="internalComplaintId" value={formik.values.internalComplaintId || ''} />
                <input type="hidden" name="customerName" value={formik.values.customerName || ''} />
                <input type="hidden" name="actionBy" value={formik.values.actionBy || ''} />
                <input type="hidden" name="createdBy" value={formik.values.createdBy || ''} />
                <input type="hidden" name="updatedBy" value={formik.values.updatedBy || ''} />
                <input type="hidden" name="createdByName" value={formik.values.createdByName || ''} />
                <input type="hidden" name="updatedByName" value={formik.values.updatedByName || ''} />
                <input type="hidden" name="mobile" value={formik.values.mobile || ''} />

                {/* Action Description */}
                <div className="field col-12 mb-3 px-2">
                  <label htmlFor="actionDescription" className="font-bold">
                    Action Description <span className="text-red-500">*</span>
                  </label>
                  <div style={fieldHoverStyle('actionDescription')}>
                    <InputTextarea
                      id="actionDescription"
                      name="actionDescription"
                      rows={5}
                      className={classNames("w-full", {
                        'p-invalid': formik.errors.actionDescription && formik.touched.actionDescription
                      })}
                      value={formik.values.actionDescription}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                    />
                  </div>
                  {formik.errors.actionDescription && formik.touched.actionDescription && (
                    <small className="p-error">{formik.errors.actionDescription as string}</small>
                  )}
                </div>

                {/* Image Upload */}
                <div className="field col-12 mb-3 px-2">
                  <ImageTemplateDemo onFileUpload={handelfileUpload} handelFileSize={handelfileSizeExceeded} />
                </div>

                {/* Remarks */}
                <div className="field col-12 mb-3 px-2">
                  <label htmlFor="remarks" className="font-bold">Remarks</label>
                  <div style={fieldHoverStyle('remarks')}>
                    <InputTextarea
                      id="remarks"
                      name="remarks"
                      rows={3}
                      className="w-full"
                      value={formik.values.remarks}
                      onChange={formik.handleChange}
                    />
                  </div>
                </div>

                {/* Corrective & Preventive Action */}
                <div className="grid w-full">
                  <div className="field col-12 sm:col-6 mb-3 px-2">
                    <label htmlFor="correctiveAction" className="font-bold">Corrective Action</label>
                    <div style={fieldHoverStyle('correctiveAction')}>
                      <InputTextarea
                        id="correctiveAction"
                        name="correctiveAction"
                        rows={4}
                        className="w-full"
                        value={formik.values.correctiveAction}
                        onChange={formik.handleChange}
                      />
                    </div>
                  </div>
                  <div className="field col-12 sm:col-6 mb-3 px-2">
                    <label htmlFor="preventiveAction" className="font-bold">Preventive Action</label>
                    <div style={fieldHoverStyle('preventiveAction')}>
                      <InputTextarea
                        id="preventiveAction"
                        name="preventiveAction"
                        rows={4}
                        className="w-full"
                        value={formik.values.preventiveAction}
                        onChange={formik.handleChange}
                      />
                    </div>
                  </div>
                </div>

                {/* Status + Force Resolved */}
                <div className="field col-12 mb-3 px-2">
                  <label htmlFor="statusId" className="font-bold">
                    Status <span className="text-red-500">*</span>
                  </label>
                  <div style={fieldHoverStyle('statusId')}>
                    <Dropdown
                      id="statusId"
                      name="statusId"
                      options={statusOptions}
                      optionLabel="label"
                      optionValue="value"
                      value={formik.values.statusId}
                      onChange={formik.handleChange}
                      className={classNames("w-full", {
                        'p-invalid': formik.errors.statusId && formik.touched.statusId
                      })}
                      disabled={forceResolved}
                    />
                  </div>
                  <div className="flex align-items-center mt-2">
                    <Checkbox
                      inputId="forceResolved"
                      checked={forceResolved}
                      onChange={(e) => {
                        setForceResolved(e.checked ?? false);
                        if (e.checked) {
                          formik.setFieldValue('statusId', 3);
                        }
                      }}
                    />
                    <label htmlFor="forceResolved" className="ml-2">Mark as Resolved</label>
                  </div>
                  {formik.errors.statusId && formik.touched.statusId && (
                    <small className="p-error">{formik.errors.statusId as string}</small>
                  )}
                </div>

                {/* Action Date & Severity */}
                <div className="grid w-full">
                  <div className="field col-12 sm:col-6 mb-3 px-2">
                    <label htmlFor="actionDate" className="font-bold">Action Date</label>
                    <div style={fieldHoverStyle('actionDate')}>
                      <InputText
                        type="date"
                        name="actionDate"
                        id="actionDate"
                        value={formik.values.actionDate}
                        onChange={formik.handleChange}
                        onFocus={(e) => e.target.showPicker?.()}
                        onKeyDown={(e) => e.preventDefault()}
                        className={classNames("w-full", {
                          'p-invalid': formik.errors.actionDate
                        })}
                      />
                    </div>
                  </div>
                  <div className="field col-12 sm:col-6 mb-3 px-2">
                    <label htmlFor="severity" className="font-bold">Severity</label>
                    <div style={fieldHoverStyle('severity')}>
                      <Dropdown
                        id="severity"
                        name="severity"
                        options={severityOptions}
                        optionLabel="label"
                        optionValue="value"
                        value={formik.values.severity}
                        onChange={formik.handleChange}
                        className="w-full"
                      />
                    </div>
                  </div>
                </div>

                {/* COPQ */}
                <div className="field col-12 sm:col-6 mb-3 px-2">
                  <label htmlFor="copqInRs" className="font-bold">COPQ in Rs</label>
                  <div style={fieldHoverStyle('copqInRs')}>
                    <InputNumber
                      placeholder="Enter COPQ in RS"
                      name="copqInRs"
                      id="copqInRs"
                      className="w-full"
                      value={formik.values.copqInRs}
                      onValueChange={(e) => formik.setFieldValue("copqInRs", e.value)}
                      locale="en-IN"
                      maxFractionDigits={2}
                    />
                  </div>
                </div>

                {/* Buttons */}
                <div className="field col-12 flex justify-content-end gap-3 mb-3 px-2">
                  <Button
                    type="button"
                    label="Reset"
                    className="p-button-secondary"
                    onClick={() => {
                      formik.resetForm();
                      setImageFile(null);
                      setForceResolved(false);
                    }}
                  />
                  <Button
                    type="submit"
                    label={isUpdate ? "Update Action" : "Submit Action"}
                    className="p-button-primary"
                    disabled={isSubmitted}
                    loading={isSubmitted}
                  />
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    </>
  );
};

export default InternalComplaintActionRegister;